import { useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import {
  AppointmentTag,
  CreateAppointmentTagRequest,
  UpdateAppointmentTagRequest,
} from "../types/appointmentTags";

/**
 * Appointment tag API methods. Self-contained hook (mirrors useClientGroups)
 * rather than growing AppContext; every call still routes through
 * `fetchWithAuth`. Scoped to the selected business location via
 * `useSalon().businessInfo.id` (multi-location-safe).
 *
 * Gating (backend): the list is open to any staff at the location; create /
 * update / delete need `calendar.canManageAppointmentTags`.
 */
export function useAppointmentTags() {
  const { fetchWithAuth } = useApp();
  const { businessInfo } = useSalon() as any;
  const businessLocationId: string | undefined = businessInfo?.id;

  /** Active tags in display order (sort_order, then name). */
  const listTags = useCallback(async (): Promise<AppointmentTag[]> => {
    if (!businessLocationId) return [];
    const response = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_TAGS, "", {
        businessLocationId,
      }),
    );
    return Array.isArray(response?.appointment_tags)
      ? response.appointment_tags
      : [];
  }, [fetchWithAuth, businessLocationId]);

  /** Create a tag. A duplicate active name (case-insensitive) rejects with status 409. */
  const createTag = useCallback(
    async (data: CreateAppointmentTagRequest): Promise<AppointmentTag> => {
      if (!businessLocationId) throw new Error("No business location selected");
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_TAGS, "", {
          businessLocationId,
        }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth, businessLocationId],
  );

  /** Partial update; `emoji: null` clears the emoji. Duplicate name → 409. */
  const updateTag = useCallback(
    async (
      tagId: string,
      data: UpdateAppointmentTagRequest,
    ): Promise<AppointmentTag> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_TAG_BY_ID, "", { tagId }),
        { method: "PUT", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  /** Soft delete: the tag leaves the catalog and every appointment; its name becomes reusable. */
  const deleteTag = useCallback(
    async (tagId: string): Promise<void> => {
      await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENT_TAG_BY_ID, "", { tagId }),
        { method: "DELETE" },
      );
    },
    [fetchWithAuth],
  );

  /**
   * Replace an appointment's tag set (`[]` clears); resolves to the tags
   * after the write. Ungated like other calendar transaction writes.
   */
  const setTransactionTags = useCallback(
    async (
      transactionId: string,
      tagIds: string[],
    ): Promise<AppointmentTag[]> => {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTION_TAGS, "", {
          transactionId,
        }),
        { method: "PUT", body: JSON.stringify({ tag_ids: tagIds }) },
      );
      return Array.isArray(response?.tags) ? response.tags : [];
    },
    [fetchWithAuth],
  );

  return {
    businessLocationId,
    listTags,
    createTag,
    updateTag,
    deleteTag,
    setTransactionTags,
  };
}

/** True when an API error is the backend's duplicate-name 409. */
export const isDuplicateTagNameError = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 409;

export default useAppointmentTags;
